import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  Chip,
} from "@mui/material";
import { User } from "../../../api";
import { ColorsEnum } from "../../../common/theme/colors";
import * as S from "../style";

interface UserTableProps {
  users: User[];
  onEdit: (user: User) => void;
  onDelete: (userId: string) => void;
}

const ROLE_LABELS: Record<string, string> = {
  superadmin: "Admin",
  clinician: "Clinician",
  user: "User",
};

const ROLE_COLORS: Record<string, string> = {
  superadmin: ColorsEnum.machoBlue,
  clinician: ColorsEnum.seeAndBeGreen,
  user: ColorsEnum.coolgray5,
};

const ROLE_TEXT_COLORS: Record<string, string> = {
  superadmin: ColorsEnum.white,
  clinician: ColorsEnum.white,
  user: ColorsEnum.gunmetal,
};

const LANGUAGE_LABELS: Record<string, string> = {
  en: "🇺🇸 English",
  no: "🇳🇴 Norwegian",
};

const UserTable: React.FC<UserTableProps> = ({ users, onEdit, onDelete }) => {
  return (
    <TableContainer component={S.StyledTableContainer}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Username</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Full Name</TableCell>
            <TableCell>Role</TableCell>
            <TableCell>Language</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((u) => {
            const uid = u.user_id || u.id || "";
            const roleLabel = ROLE_LABELS[u.role ?? ""] ?? u.role ?? "User";
            const roleColor = ROLE_COLORS[u.role ?? ""] ?? ColorsEnum.coolgray5;
            return (
              <TableRow key={uid} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {u.username}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {u.email}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {u.full_name || "—"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={roleLabel}
                    size="small"
                    sx={{
                      bgcolor: roleColor,
                      color: ROLE_TEXT_COLORS[u.role ?? ""] ?? ColorsEnum.gunmetal,
                      fontWeight: 600,
                      borderRadius: "12px",
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {LANGUAGE_LABELS[u.language] ?? u.language ?? "—"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={u.is_active !== false ? "Active" : "Inactive"}
                    size="small"
                    sx={{
                      bgcolor:
                        u.is_active !== false
                          ? ColorsEnum.green
                          : ColorsEnum.error,
                      color: ColorsEnum.white,
                      fontWeight: 600,
                      borderRadius: "12px",
                    }}
                  />
                </TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => onEdit(u)}
                    sx={{ mr: 1 }}
                  >
                    Edit
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => onDelete(uid)}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
          {users.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>
                No users found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default UserTable;
